import React from 'react';
import {Alert,Box,Button,Stack,TextField,Typography} from '@mui/material';
import type {ContentCommandState,ContentReviewItem} from '../../services/contentReviewService';
import {ApiError} from '../../lib/api';
import {useCoordinatedRefresh} from '../../hooks/useCoordinatedRefresh';
import {DEFAULT_FOOTAGE_REASON,MANUAL_FOOTAGE_MODE,manualFootageEnabled,manualFootageService,
 readPendingFootage,savePendingFootage,sameFootageRequest,selectedFootage,validFootageReason,
 manualFootageOwnerCurrent,type ManualFootageOwner,type FootageOccurrence,type ManualFootageSnapshot,type PendingFootageRequest} from '../../services/manualFootageService';

interface Props {
 owner:ManualFootageOwner|null;
 item:Pick<ContentReviewItem,'id'|'fingerprint'|'revision_capable'|'status'|'verdict'|'command'>;blocked:boolean;
 onSeek:(offsetSec:number)=>void;
 onPending:(pending:PendingFootageRequest)=>void;
 onConfirmed:(pending:PendingFootageRequest,state:ContentCommandState)=>void;
 onLock:(locked:boolean)=>void;
 onRefresh:()=>void;
}
/** Separate from generic review dials: the note is retained, never interpreted. */
export default function ManualFootageControl({item,owner,blocked,onSeek,onPending,onConfirmed,onLock,onRefresh}:Props) {
 const [snapshot,setSnapshot]=React.useState<ManualFootageSnapshot|null>(null);
 const [selected,setSelected]=React.useState<FootageOccurrence|null>(null);
 const [note,setNote]=React.useState('');
 const [pending,setPending]=React.useState<PendingFootageRequest|null>(null);
 const [error,setError]=React.useState<string|null>(null);
 const [ready,setReady]=React.useState(false);
 const [busy,setBusy]=React.useState(false);
 const lock=React.useRef(false);
 const epoch=React.useRef(0);
 const callbacks=React.useRef({onPending,onConfirmed,onLock});
 callbacks.current={onPending,onConfirmed,onLock};
 const key=JSON.stringify([item.id,item.fingerprint,owner]);
 const [loadedKey,setLoadedKey]=React.useState<string|null>(null);
 const current=key===loadedKey;
 const load=React.useCallback(async()=>{
  const attempt=++epoch.current;
  lock.current=false;setBusy(false);setReady(false);setError(null);setSnapshot(null);setSelected(null);
  try {
   if(!manualFootageOwnerCurrent(owner))throw new Error('Sign in to check footage replacement for this account.');
   const saved=readPendingFootage(owner).find(r=>r.packageId===item.id)??null;
   setPending(saved);setNote(saved?.request.reason??'');
   callbacks.current.onLock(Boolean(saved));
   if(!manualFootageEnabled())return;
   if(saved?.confirmedState) {setLoadedKey(key);setReady(true);return;}
   const [fresh,queued]=await Promise.all([manualFootageService.read(item.id,owner),manualFootageService.queued(item.id,owner)]);
   if(attempt!==epoch.current||!manualFootageOwnerCurrent(owner))return;
   if(queued.queued) {
    if(saved && !sameFootageRequest(saved.request,queued.queued))throw new Error('Another footage request is pending. Reload the reel to review its status.');
    const q=queued.queued;
    const confirmed=saved??{packageId:item.id,previousRequestId:item.command?.request_id??null,request:{request_id:q.request_id,
     schema_version:1,fingerprint:q.fingerprint,scene_id:q.scene_id,clip_id:q.clip_id,clip_index:q.clip_index,selection_mode:q.selection_mode,reason:q.reason}};
    callbacks.current.onConfirmed(confirmed,q.state);return;
   }
   if(!saved && fresh.fingerprint!==item.fingerprint)throw new Error('This reel changed. Close and reload before selecting footage.');
   setSnapshot(fresh);setLoadedKey(key);setReady(true);
  } catch(e) {if(attempt===epoch.current&&manualFootageOwnerCurrent(owner))setError(e instanceof ApiError || !(e instanceof Error)
    ? 'Footage replacement could not be checked. It may be disabled or temporarily unavailable. Close and reload to try again.'
    : e.message);}
 },[item.id,item.fingerprint,item.command?.request_id,key,owner]);
 React.useEffect(()=>{
  void load();
  return()=>{epoch.current++;callbacks.current.onLock(false);};
 },[load]);
 const checkQueued=React.useCallback(async()=>{
  if(!pending || pending.confirmedState || lock.current || !manualFootageEnabled() || !manualFootageOwnerCurrent(owner))return;
  const attempt=epoch.current;
  try {const result=await manualFootageService.queued(item.id,owner);
   if(attempt!==epoch.current || !manualFootageOwnerCurrent(owner) || !result.queued)return;
   if(!sameFootageRequest(pending.request,result.queued))return;
   callbacks.current.onConfirmed(pending,result.queued.state);
  }catch{/* An absent or failed read is not proof that the write failed. */}
 },[item.id,pending,owner]);
 useCoordinatedRefresh(checkQueued,{enabled:Boolean(pending)&&!pending?.confirmedState&&!busy,pollMs:10000,requestOnMount:false});
 const submit=async()=>{
  if(pending?.confirmedState || lock.current || !current || !ready || blocked || !manualFootageOwnerCurrent(owner))return;
  let sent=pending;
  if(!sent) {
   if(!snapshot?.editable || !selected)return;
   try {
    selectedFootage(snapshot,item.id,item.fingerprint??'',selected);
    const reason=note.trim()?note:DEFAULT_FOOTAGE_REASON;
    if(!validFootageReason(reason))throw new Error('Use a review note of at most 2,000 valid text characters.');
    sent={packageId:item.id,previousRequestId:item.command?.request_id??null,request:{request_id:crypto.randomUUID(),schema_version:1,
     fingerprint:snapshot.fingerprint,scene_id:selected.scene_id,clip_id:selected.clip_id,clip_index:selected.clip_index,
     selection_mode:MANUAL_FOOTAGE_MODE,reason}};
    savePendingFootage(sent,owner);
   }catch{setError('Could not save or verify this request. Nothing was submitted. Reload before trying again.');return;}
  }
  const attempt=epoch.current;lock.current=true;setBusy(true);setError(null);setPending(sent);
  callbacks.current.onPending(sent);callbacks.current.onLock(true);
  try {
   const result=await manualFootageService.replace(item.id,sent.request,owner);
   if(!manualFootageOwnerCurrent(owner))return;
   if(['queued','leased','retry','applied'].includes(result.state)) {
    const confirmed={...sent,confirmedState:result.state as PendingFootageRequest['confirmedState']};
    // Closing a sheet does not erase a known acceptance. Update only the same
    // still-saved identity; never revive an already reconciled request.
    const stored=readPendingFootage(owner).find(row=>row.packageId===item.id);
    if(stored && sameFootageRequest(stored.request,sent.request))savePendingFootage(confirmed,owner);
    if(attempt!==epoch.current)return;
    setPending(confirmed);
    callbacks.current.onConfirmed(confirmed,result.state);
   } else if(attempt===epoch.current)callbacks.current.onConfirmed(sent,result.state);
  }catch{
   if(attempt===epoch.current&&manualFootageOwnerCurrent(owner))setError('Could not confirm the request. Retry checks the same saved request. Your review note and selected clip are locked until its status is known.');
  }finally{if(attempt===epoch.current){lock.current=false;setBusy(false);}}
 };
 if(!manualFootageEnabled())return null;
 if(!manualFootageOwnerCurrent(owner))return <Alert severity="info">Sign in to check footage replacement for this account.</Alert>;
 const eligible=current && ready && snapshot?.editable && item.verdict==='pending' && item.revision_capable
  && ['awaiting_review','revised','qa_failed'].includes(item.status) && snapshot.fingerprint===item.fingerprint;
 const invalidNote=!!note.trim()&&!validFootageReason(note);
 return <Box sx={{my:2}} data-testid="manual-footage-control">
  <Typography variant="h6">Replace one clip</Typography>
  <Typography variant="body2">Choose a timed clip occurrence. The engine will try another eligible clip matching the same scene. Words, timing and other occurrences stay unchanged. Review the new video before approving.</Typography>
  {error?<Alert severity="warning">{error}</Alert>:null}
  {!ready&&!error?<Typography>Checking footage replacement availability…</Typography>:null}
  {ready&&!eligible&&!pending?<Typography>Footage replacement is unavailable for this reel.</Typography>:null}
  {current&&pending?.confirmedState?<Alert severity="info">
    {pending.confirmedState==='applied'?'Replacement applied; waiting for the new video to reach Review.':'Replacement accepted; waiting for the Content engine.'} This saved request is read-only. Checking status does not request another render.
    <Button onClick={onRefresh}>Refresh replacement status</Button>
   </Alert>:null}
  {current&&pending&&!pending.confirmedState?<Alert severity="info">Saved request for scene {pending.request.scene_id}, clip {pending.request.clip_id}, occurrence {pending.request.clip_index+1}. Check its current status or retry the same request identity. Closing this sheet keeps its request identity for this tab.</Alert>:null}
  {eligible&&!pending?<Box role="group" aria-label="Select a clip occurrence to replace">
   {snapshot!.footage_targets.map(t=><Button key={t.clip_index} disabled={busy||blocked}
    aria-pressed={selected?.clip_index===t.clip_index} variant={selected?.clip_index===t.clip_index?'contained':'outlined'}
    sx={{m:0.5,minHeight:44}} onClick={()=>{setSelected(t);onSeek(t.offset_sec);}}>
    {t.scene_id} · clip {t.clip_id} · occurrence {t.clip_index+1} · {t.offset_sec.toFixed(1)}–{(t.offset_sec+t.duration_sec).toFixed(1)}s
   </Button>)}
  </Box>:null}
  {eligible||(current&&pending)?<Stack spacing={1} sx={{mt:1}}>
   <TextField label="Review note (optional, audit only)" multiline value={note} disabled={busy||!!pending||blocked}
    onChange={e=>setNote(e.target.value)} error={invalidNote}
    helperText={`Saved with the request; it does not direct clip selection or generate footage. ${Array.from(note).length}/2,000 characters.`}/>
   {!pending?.confirmedState?<Button variant="contained" disabled={busy||blocked||!ready||!current||(!pending&&(!eligible||!selected||invalidNote))} onClick={()=>void submit()}>
    {busy?'Requesting replacement…':pending?'Retry same replacement request':'Replace selected occurrence with an alternate clip'}
   </Button>:null}
  </Stack>:null}
  {error&&!ready?<Button onClick={()=>void load()} disabled={busy}>Check replacement availability</Button>:null}
 </Box>;
}
